import { useEffect, useMemo, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import { DefaultSpinnerCenter } from '@components/Loader/Loader';
import { ConfigureNumbers } from '@components/Order/Numbers/CheckoutNumbers/ConfigureNumbers';
import ProfilesForPlan from '@components/Order/Numbers/CheckoutNumbers/ProfilesForPlan';
import { Page500 } from '@containers/ErrorPages';
import { capacityApi } from '@reducers/capacity';
import { cartApi, didServiceTypeApi } from '@reducers/order';
import { planDetailsApi } from '@reducers/plan';
import { profileApi } from '@reducers/profile/profile';
import { skipToken } from '@reduxjs/toolkit/query';

const ApplyToAll = () => {
  const { values, setFieldValue } = useFormikContext();
  useEffect(() => {
    if (!values.all.profileID) return;
    Object.keys(values.dids).forEach((item) => {
      setFieldValue(`dids.${item}.profileID`, values.all.profileID);
    });
  }, [values.all.profileID]);
  useEffect(() => {
    if (!values.all.didServiceType) return;
    Object.keys(values.dids).forEach((item) =>
      setFieldValue(`dids.${item}.didServiceType`, values.all.didServiceType)
    );
  }, [values.all.didServiceType]);
  useEffect(() => {
    if (!values.all.trunkID) return;
    Object.keys(values.dids).forEach((item) =>
      setFieldValue(`dids.${item}.trunkID`, values.all.trunkID)
    );
  }, [values.all.trunkID]);
  return null;
};

const CheckoutForPlan = ({ queries = {} }) => {
  const {
    useCartQuery,
    useSaveCartBackordersMutation,
    useSaveCartDidsMutation,
  } = queries;
  const [formReady, setFormReady] = useState(false);
  const { id, type, prefixID } = useParams();
  const navigate = useNavigate();

  const queryArg = id && prefixID ? { id, type, prefixID } : skipToken;
  const cart = useCartQuery(queryArg);

  const profiles =
    planDetailsApi.useGetProfilesAgainstRequirementsQuery(queryArg);

  const saveDids = useSaveCartDidsMutation();
  const saveBackorders = useSaveCartBackordersMutation();
  // Load options
  const profilesOptions = profileApi.useGetForSelectWithoutBlockedQuery();
  const useCaseOptions = didServiceTypeApi.useGetForSelectQuery();
  const trunksOptions = capacityApi.useGetUserTrunksForSelectQuery();

  const isLoading =
    cart.isLoading ||
    cart.isFetching ||
    profiles.isLoading ||
    profiles.isFetching ||
    profilesOptions.isLoading ||
    profilesOptions.isFetching ||
    useCaseOptions.isLoading ||
    useCaseOptions.isFetching ||
    trunksOptions.isLoading ||
    trunksOptions.isFetching;

  useEffect(() => {
    if (!formReady && !isLoading && cart.data) {
      setFormReady(true);
    }
  }, [isLoading, cart.data, formReady]);

  const didsObjects = useMemo(() => {
    const obj = {};
    if (!cart.data) {
      return obj;
    }
    cart.data.forEach((item) => {
      obj[`did-${item.id}`] = {
        ...item,
        profileID: item.end_user_id != null ? String(item.end_user_id) : '',
        trunkID: item.trunk_id != null ? String(item.trunk_id) : '',
        didServiceType:
          item.use_case_id != null ? String(item.use_case_id) : '',
      };
    });
    return obj;
  }, [cart.data]);

  const schemaObjects = useMemo(() => {
    const obj = {};
    if (!cart.data) {
      return obj;
    }
    cart.data.forEach((item) => {
      obj[`did-${item.id}`] = Yup.object().shape({
        profileID: Yup.string().label('Profile').required(),
        trunkID: Yup.string().label('Trunk').required(),
        didServiceType: Yup.string().label('DID Service type').required(),
      });
    });
    return obj;
  }, [cart.data]);

  if (!formReady) {
    if (isLoading) return <DefaultSpinnerCenter />;
    if (cart.isError || profiles.isError) return <Page500 />;
    if (cart.data && cart.data.length === 0) return navigate('/');
    return <DefaultSpinnerCenter />;
  }

  const validationSchema = Yup.object().shape({
    dids: Yup.object().shape({ ...schemaObjects }),
  });
  const onSubmit = async (values, actions) => {
    const [triggerDids] = saveDids;
    const [triggerBackorders] = saveBackorders;

    actions.setSubmitting(true);
    function filterObject(obj, callback) {
      return Object.fromEntries(
        Object.entries(obj).filter(([key, val]) => callback(key, val))
      );
    }
    const didData = filterObject(
      values.dids,
      (_, { is_backorder: isBackorder }) => !isBackorder
    );
    const backorderData = filterObject(
      values.dids,
      (_, { is_backorder: isBackorder }) => isBackorder
    );
    const getRedirectPath = () =>
      values.checkout ? '/cart/checkout' : '/order/numbers';
    if (Object.entries(didData).length > 0 && !values.configured) {
      triggerDids({ data: didData, id, type, prefixID })
        .unwrap()
        .then(() => {
          navigate(getRedirectPath());
        })
        .catch((error) => {
          actions.setErrors(error);
          actions.setSubmitting(false);
        });
    }
    if (Object.entries(backorderData).length > 0) {
      triggerBackorders({ data: backorderData, id, type, prefixID })
        .unwrap()
        .then(() => {
          navigate(getRedirectPath());
        })
        .catch((error) => {
          actions.setErrors(error);
          actions.setSubmitting(false);
        });
    }
    if (values.configured) {
      navigate(getRedirectPath());
    }
  };
  return (
    <>
      <Row style={{ marginBottom: '10px' }}>
        <Col md={12}>
          <Formik
            validationSchema={validationSchema}
            initialValues={{
              checkout: true,
              all: { profileID: '', trunkID: '', didServiceType: '' },
              dids: didsObjects,
            }}
            onSubmit={onSubmit}
          >
            {({ handleSubmit }) => (
              <Form onSubmit={handleSubmit}>
                <ConfigureNumbers data={cart.data} queries={queries} />
                <ApplyToAll />
              </Form>
            )}
          </Formik>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <ProfilesForPlan data={profiles.data} />
        </Col>
      </Row>
    </>
  );
};

export const CheckoutNumbersForPlan = () => {
  const location = useLocation();
  const { configured } = location?.state ? location.state : {};
  const cartQuery = configured
    ? cartApi.useGetConfiguredForPlanQuery
    : cartApi.useGetForPlanQuery;
  return CheckoutForPlan({
    queries: {
      useCartQuery: cartQuery,
      useSaveCartDidsMutation: cartApi.useSaveDidsFromCartMutation,
      useSaveCartBackordersMutation: cartApi.useSaveBackordersFromCartMutation,
      useDeleteDidMutation: cartApi.useRemoveDidFromCartMutation,
      useDeleteBackorderMutation: cartApi.useRemoveBackorderFromCartMutation,
    },
  });
};

export const CheckoutPSTNUpgrade = () => {
  const queries = {
    useCartQuery: cartApi.useGetForPSTNUpgradeQuery,
    useSaveCartDidsMutation: cartApi.useSaveDidsFromCartMutation,
    useDeleteDidMutation: cartApi.useRemovePSTNUpgradeDidFromCartMutation,
  };
  const { useCartQuery, useSaveCartDidsMutation } = queries;
  const { login } = useSelector((state) => state.auth.user);
  const navigate = useNavigate();
  const { id, type, prefixID } = useParams();
  const queryArg = id ? { id, type, customer: login } : skipToken;
  const cart = useCartQuery(queryArg);

  const profiles =
    planDetailsApi.useGetProfilesAgainstRequirementsQuery(queryArg);

  const saveDids = useSaveCartDidsMutation();

  const isLoading =
    cart.isLoading ||
    cart.isFetching ||
    profiles.isLoading ||
    profiles.isFetching;
  if (isLoading) {
    return <DefaultSpinnerCenter />;
  }
  if (cart.isError || profiles.isError) {
    return <Page500 />;
  }
  if (cart.data && cart.data.length === 0) {
    return navigate('/');
  }

  const didsObjects = {};
  const schemaObjects = {};
  cart.data.forEach((item) => {
    didsObjects[`did-${item.id}`] = {
      ...item,
      profileID: '',
      trunkID: '',
      didServiceType: '',
    };
    schemaObjects[`did-${item.id}`] = Yup.object().shape({
      profileID: Yup.string().label('Profile').required(),
      trunkID: Yup.string().label('Trunk').required(),
      didServiceType: Yup.string().label('DID Service type').required(),
    });
  });
  const validationSchema = Yup.object().shape({
    dids: Yup.object().shape({ ...schemaObjects }),
  });
  const onSubmit = async (values, actions) => {
    const [triggerDids] = saveDids;

    actions.setSubmitting(true);
    function filterObject(obj, callback) {
      return Object.fromEntries(
        Object.entries(obj).filter(([key, val]) => callback(key, val))
      );
    }
    const didData = filterObject(
      values.dids,
      (_, { is_backorder: isBackorder }) => !isBackorder
    );
    const getRedirectPath = () =>
      values.checkout ? '/cart/checkout' : '/order/pstn-upgrade';
    if (Object.entries(didData).length > 0) {
      triggerDids({ data: didData, id, type, prefixID })
        .unwrap()
        .then(() => {
          navigate(getRedirectPath());
        })
        .catch((error) => {
          actions.setErrors(error);
          actions.setSubmitting(false);
        });
    }
  };
  return (
    <>
      <Row style={{ marginBottom: '10px' }}>
        <Col md={12}>
          <Formik
            validationSchema={validationSchema}
            initialValues={{
              checkout: true,
              all: { profileID: '', trunkID: '', didServiceType: '' },
              dids: didsObjects,
            }}
            onSubmit={onSubmit}
          >
            {({ handleSubmit }) => (
              <Form onSubmit={handleSubmit}>
                <ConfigureNumbers data={cart.data} queries={queries} />
                <ApplyToAll />
              </Form>
            )}
          </Formik>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <ProfilesForPlan data={profiles.data} />
        </Col>
      </Row>
    </>
  );
};
